import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Upload } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;

const SubCategories = () => {
    const [subCategories, setSubCategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [editingSubCategory, setEditingSubCategory] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchSubCategories();
        fetchCategories(); // Load categories for the dropdown
    }, []);

    const fetchSubCategories = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/sub-categories');
            if (response.data.subCategories) {
                setSubCategories(response.data.subCategories);
            } else {
                message.error('Failed to fetch sub-categories.');
                setSubCategories([]);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch sub-categories.');
            setSubCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories) {
                setCategories(response.data.categories);
            } else {
                message.error('Failed to fetch categories.');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to fetch categories.');
        }
    };

    const showModal = (subCategory = null) => {
        if (subCategory) {
            setEditingSubCategory(subCategory);
            form.setFieldsValue({
                name: subCategory.name,
                category_id: subCategory.category.id,
            });
        } else {
            setEditingSubCategory(null);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleOk = async (values) => {
        try {
            let response;
            if (editingSubCategory) {
                response = await axiosInstance.put(`/sub-categories/${editingSubCategory.id}`, values);
                message.success('Sub-category updated successfully');
            } else {
                response = await axiosInstance.post('/sub-categories', values);
                message.success('Sub-category added successfully');
            }
            fetchSubCategories();
            setIsModalVisible(false);
            form.resetFields();
        } catch (error) {
            message.error('Failed to save sub-category.');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    // Generate Excel template for subcategories
    // In your React component, update the downloadTemplate function
const downloadTemplate = async () => {
    try {
        // Get existing categories first
        let existingCategories = [];
        try {
            const response = await axiosInstance.get('/categories');
            if (response.data.categories && response.data.categories.length > 0) {
                existingCategories = response.data.categories.slice(0, 4);
            }
        } catch (error) {
            console.warn('Could not fetch categories for template');
        }

        // Create proper CSV content
        let csvContent = 'Name,Category Name\n'; // Headers
        
        if (existingCategories.length > 0) {
            const samples = ['Mobile Phones', 'T-Shirts', 'Soft Drinks', 'Laptops'];
            existingCategories.forEach((category, index) => {
                if (index < samples.length) {
                    csvContent += `${samples[index]},${category.name}\n`;
                }
            });
        } else {
            csvContent += 'Mobile Phones,Electronics\n';
            csvContent += 'T-Shirts,Clothing\n';
            csvContent += 'Soft Drinks,Food & Beverages\n';
        }

        // Create blob with proper MIME type
        const blob = new Blob([csvContent], { type: 'text/csv' });
        
        // Download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'subcategories_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        message.success('Template downloaded successfully');
    } catch (error) {
        console.error('Download error:', error);
        message.error('Failed to download template');
    }
};

    // Alternative Excel template using SheetJS if available
    const downloadExcelTemplate = () => {
        try {
            if (typeof window.XLSX !== 'undefined') {
                const wb = window.XLSX.utils.book_new();
                const ws = window.XLSX.utils.aoa_to_sheet([
                    ['Name', 'Category Name'],
                    ['Mobile Phones', 'Electronics'],
                    ['T-Shirts', 'Clothing'],
                    ['Soft Drinks', 'Food & Beverages'],
                    ['Laptops', 'Electronics']
                ]);
                
                window.XLSX.utils.book_append_sheet(wb, ws, 'SubCategories');
                window.XLSX.writeFile(wb, 'subcategories_template.xlsx');
                message.success('Excel template downloaded successfully');
            } else {
                downloadTemplate();
            }
        } catch (error) {
            console.error('Excel download error:', error);
            downloadTemplate();
        }
    };

    // Handle file upload
    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.error('Please select a file to upload');
            return;
        }

        const formData = new FormData();
        formData.append('file', fileList[0]);

        setUploading(true);
        try {
            const response = await axiosInstance.post('/sub-categories/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.message) {
                message.success(response.data.message);
                setImportModalVisible(false);
                setFileList([]);
                fetchSubCategories();
            }

            // Handle import errors if any
            if (response.data.errors && response.data.errors.length > 0) {
                message.warning(`Import completed with ${response.data.errors.length} errors`);
                
                // Show detailed errors
                Modal.info({
                    title: 'Import Errors',
                    content: (
                        <div>
                            <p>The following errors occurred during import:</p>
                            <ul>
                                {response.data.errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                    width: 600,
                });
            }

        } catch (error) {
            console.error('Upload error:', error);
            
            // Handle missing categories specifically
            if (error.response?.data?.missing_categories) {
                const missingCategories = error.response.data.missing_categories;
                
                Modal.confirm({
                    title: 'Missing Categories Found',
                    width: 700,
                    content: (
                        <div>
                            <p style={{ marginBottom: '16px', color: '#ff4d4f' }}>
                                <strong>Import cancelled!</strong> The following categories do not exist in your shop:
                            </p>
                            <div style={{ 
                                background: '#fff2f0', 
                                border: '1px solid #ffccc7', 
                                borderRadius: '6px', 
                                padding: '12px',
                                marginBottom: '16px'
                            }}>
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {missingCategories.map((category, index) => (
                                        <li key={index} style={{ color: '#cf1322', fontWeight: '500' }}>
                                            {category}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <p style={{ marginBottom: '8px' }}>
                                <strong>What you need to do:</strong>
                            </p>
                            <ol style={{ paddingLeft: '20px', margin: 0 }}>
                                <li>Go to the Categories page</li>
                                <li>Create the missing categories listed above</li>
                                <li>Return here and try importing again</li>
                            </ol>
                            <p style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
                                <strong>Note:</strong> No sub-categories were imported to maintain data integrity.
                            </p>
                        </div>
                    ),
                    okText: 'Go to Categories',
                    cancelText: 'Close',
                    onOk: () => {
                        // Navigate to categories page - adjust this route as needed
                        window.location.href = '/categories'; // or use your router
                    },
                });
                
                // Also show a summary message
                message.error(`Import cancelled - ${missingCategories.length} missing categories found`);
                
            } else if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat();
                message.error('Validation errors: ' + errorMessages.join(', '));
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('Failed to upload file');
            }
        } finally {
            setUploading(false);
        }
    };

    // Upload props
    const uploadProps = {
        onRemove: (file) => {
            const index = fileList.indexOf(file);
            const newFileList = fileList.slice();
            newFileList.splice(index, 1);
            setFileList(newFileList);
        },
        beforeUpload: (file) => {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['xlsx', 'xls', 'csv'];
            
            if (!allowedExtensions.includes(fileExtension)) {
                message.error('You can only upload Excel files (.xlsx, .xls) or CSV files!');
                return false;
            }

            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error('File must be smaller than 5MB!');
                return false;
            }

            setFileList([file]);
            return false;
        },
        fileList,
        accept: '.xlsx,.xls,.csv'
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Category',
            dataIndex: ['category', 'name'],
            key: 'category',
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, subCategory) => (
                <Button onClick={() => showModal(subCategory)}>Edit</Button>
            ),
        },
    ];

    return (
        <div>
            <div style={{ textAlign: 'right', marginBottom: 16, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button 
                    icon={<DownloadOutlined />}
                    onClick={downloadExcelTemplate}
                >
                    Download Template
                </Button>
                <Button 
                    icon={<UploadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                >
                    Import Sub-Categories
                </Button>
                <Button type="primary" onClick={() => showModal()}>
                    Add Sub-Category
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={Array.isArray(subCategories) ? subCategories : []}
                rowKey="id"
                pagination={{ pageSize: 25 }}
                scroll={{ x: 'max-content' }}
                loading={loading}
            />

            {/* Add/Edit SubCategory Modal */}
            <Modal
                title={editingSubCategory ? 'Edit Sub-Category' : 'Add New Sub-Category'}
                visible={isModalVisible}
                onOk={() => form.submit()}
                onCancel={handleCancel}
                okText={editingSubCategory ? 'Update' : 'Add'}
                cancelText="Cancel"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleOk}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Please input the sub-category name!' }]}
                    >
                        <Input placeholder="Enter sub-category name" />
                    </Form.Item>
                    <Form.Item
                        name="category_id"
                        label="Category"
                        rules={[{ required: true, message: 'Please select a category!' }]}
                    >
                        <Select placeholder="Select a category">
                            {categories.map((category) => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Import SubCategories Modal */}
            <Modal
                title="Import Sub-Categories"
                visible={importModalVisible}
                onOk={handleUpload}
                onCancel={() => {
                    setImportModalVisible(false);
                    setFileList([]);
                }}
                okText="Upload"
                cancelText="Cancel"
                confirmLoading={uploading}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>Upload an Excel file (.xlsx, .xls) or CSV file to import multiple sub-categories at once.</p>
                    <Button 
                        type="link" 
                        onClick={downloadExcelTemplate}
                        style={{ padding: 0 }}
                    >
                        Download template file
                    </Button>
                </div>
                
                <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>

                <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                    <p><strong>Supported formats:</strong> XLSX, XLS, CSV</p>
                    <p><strong>Max file size:</strong> 5MB</p>
                    <p><strong>Required columns:</strong> Name, Category Name</p>
                    <p><strong>Note:</strong></p>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        <li>If a category doesn't exist, it will be created automatically</li>
                        <li>Delete sample data rows before adding your own data</li>
                        <li>Category Name must match exactly (case-insensitive)</li>
                    </ul>
                </div>
            </Modal>
        </div>
    );
};

export default SubCategories;